// src/services/formSubmissionService.ts

import {
  FormSubmissionRequest,
  FormSubmissionResponse,
  FormSubmissionError,
  isFormSubmissionSuccess,
} from "../interfaces/formTypes";
import { createLogger } from "../utils/logger";
import { constructRequest } from "../api/utils/apiHandler";
import { handleAuthRejection } from "../api/utils/errorHandler";
import { createClientErrorInfo } from "../api/utils/clientErrorHandler";

const log = createLogger("FormSubmissionService");

// Use environment variable for API base URL
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for form submission errors
 */
export class FormSubmissionApiError extends Error {
  errorCode: string;
  statusCode: number;
  fieldErrors?: Record<string, string>;

  constructor(
    message: string,
    errorCode: string,
    statusCode: number,
    fieldErrors?: Record<string, string>
  ) {
    super(message);
    this.name = "FormSubmissionApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Submit a workflow form to the backend
 *
 * @param endpoint - The submission endpoint (e.g., "/api/workflows/nda-approval")
 * @param request - The form submission request payload
 * @param accessToken - Optional access token for authentication
 * @returns Promise with form submission response
 */
export async function submitWorkflowForm(
  endpoint: string,
  request: FormSubmissionRequest,
  accessToken?: string
): Promise<FormSubmissionResponse> {
  // Construct full URL - endpoint may be relative or absolute
  // Handle case where endpoint starts with /api but API_BASE_URL already ends with /api
  let normalizedEndpoint = endpoint;
  if (normalizedEndpoint.startsWith("/api/") && API_BASE_URL?.endsWith("/api")) {
    normalizedEndpoint = normalizedEndpoint.substring(4); // Remove leading /api
  }
  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE_URL}${normalizedEndpoint}`;

  log.info("Submitting workflow form", {
    url,
    formType: request.formType,
    formInstanceId: request.formInstanceId,
    fieldCount: Object.keys(request.data).length,
    documentUrl: request.documentUrl,
  });

  try {
    // Create JSON body for the request
    const body = JSON.stringify(request);

    // Use constructRequest utility to handle authorization
    // Pass undefined for formData since we're using JSON body
    const apiRequest = constructRequest(url, "POST", accessToken || null, undefined);

    // Override content-type for JSON
    const headers = new Headers(apiRequest.headers);
    headers.set("Content-Type", "application/json");

    const response = await fetch(apiRequest.url, {
      method: "POST",
      headers,
      body,
    });

    // Emit the auth-rejection telemetry and, on 401, trigger the token refresh
    // bridge. Called BEFORE the body is consumed so a non-JSON 401 body (e.g.
    // a WAF block page) still triggers the refresh.
    handleAuthRejection(response);

    // Parse response
    const data = await response.json();

    if (!response.ok) {
      log.error("Form submission API returned error", {
        status: response.status,
        data,
      });

      // FastAPI HTTPException wraps error info in 'detail' field
      // Handle both wrapped and unwrapped formats for compatibility
      const errorResponse: FormSubmissionError =
        data.detail && typeof data.detail === "object"
          ? (data.detail as FormSubmissionError)
          : (data as FormSubmissionError);

      throw new FormSubmissionApiError(
        errorResponse.error || "Failed to submit form",
        "FORM_SUBMISSION_ERROR",
        response.status,
        errorResponse.fieldErrors
      );
    }

    // Validate successful response
    if (isFormSubmissionSuccess(data)) {
      log.info("Form submitted successfully", {
        formType: request.formType,
        formInstanceId: request.formInstanceId,
        message: data.message,
      });

      return data;
    }

    // Unexpected response format
    log.warn("Unexpected response format from form submission", { data });
    return { success: true, message: "Form submitted" };
  } catch (error) {
    // If it's already a FormSubmissionApiError, rethrow it
    if (error instanceof FormSubmissionApiError) {
      throw error;
    }

    // Handle client-side errors with helpful, contextual messages
    log.error("Failed to submit workflow form", {
      error: error instanceof Error ? error.message : String(error),
      formType: request.formType,
      formInstanceId: request.formInstanceId,
    });

    const clientError = createClientErrorInfo(error, "general");
    throw new FormSubmissionApiError(clientError.message, clientError.errorCode, 0);
  }
}
